import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";

const ADD = "__add__";
let cache = null;
const listeners = new Set();

export function useUnits() {
  const [units, setUnits] = useState(cache || []);
  useEffect(() => {
    listeners.add(setUnits);
    if (!cache) api.get("/units").then((r) => { cache = r.data.units; listeners.forEach((fn) => fn(cache)); }).catch(() => {});
    return () => listeners.delete(setUnits);
  }, []);
  const add = async (unit) => {
    const r = await api.post("/units", { unit });
    cache = r.data.units;
    listeners.forEach((fn) => fn(cache));
  };
  return { units, add };
}

export default function UnitSelect({ value, onChange, className = "", testId }) {
  const { units, add } = useUnits();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  // "BOX" on the invoice must select the "box" option: resolve the value case-insensitively
  const match = value ? units.find((u) => u.toLowerCase() === value.toLowerCase()) : null;
  const selected = match || value || "";
  const options = value && !match ? [value, ...units] : units;

  const save = async () => {
    const unit = draft.trim();
    if (!unit) return setAdding(false);
    try {
      await add(unit);
      onChange(unit);
      setAdding(false);
      setDraft("");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (adding) {
    return (
      <div className="flex items-center gap-1">
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. crate"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") setAdding(false); }}
          data-testid={`${testId}-new`} className="w-full min-w-[70px] rounded border border-emerald-400 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <button type="button" onClick={save} className="p-1 text-emerald-700" data-testid={`${testId}-save`}><Check className="h-4 w-4" /></button>
        <button type="button" onClick={() => setAdding(false)} className="p-1 text-slate-400" data-testid={`${testId}-cancel`}><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <select value={selected} data-testid={testId}
      onChange={(e) => { if (e.target.value === ADD) { setAdding(true); } else onChange(e.target.value); }}
      className={`w-full rounded border px-1.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${className || "border-slate-200 bg-white"}`}>
      <option value="">—</option>
      {options.map((u) => <option key={u} value={u}>{u}</option>)}
      <option value={ADD}>+ Add unit…</option>
    </select>
  );
}
